import { useEffect, useRef, useState } from 'react';
import { Alert } from '../../components/ui.tsx';
import { humanSize } from './fileRules.ts';
import { uploadAll, type StagedFile } from './uploadQueue.ts';

const TONE: Record<string, string> = {
  pending: 'text-ink-500', uploading: 'text-brand-700', done: 'text-emerald-700', failed: 'text-rose-700',
};
const LABEL: Record<string, string> = {
  pending: 'بانتظار الرفع', uploading: 'جارٍ الرفع…', done: 'تم الرفع', failed: 'فشل الرفع',
};

/**
 * مرحلة ما بعد إنشاء الحجز: الحجز محفوظ بالفعل ولا يتأثر بأي فشل في الرفع (P-5).
 * كل ملف يرفع على حدة بحالته الخاصة، والفاشل قابل لإعادة المحاولة هنا أو لاحقًا من تفاصيل الحجز.
 */
export function CreatedWithUploads({ bookingId, bookingNo, files, onFinish }:
  { bookingId: string; bookingNo: string; files: StagedFile[]; onFinish: () => void }) {
  const [items, setItems] = useState<StagedFile[]>(files);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  const run = async (list: StagedFile[]) => {
    setBusy(true);
    await uploadAll(bookingId, list, f => setItems(s => s.map(x => (x.key === f.key ? f : x))));
    setBusy(false);
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void run(files);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const failed = items.filter(f => f.state === 'failed');
  const done = items.filter(f => f.state === 'done').length;

  return (
    <div className="space-y-3">
      <Alert kind="success">تم إنشاء الحجز {bookingNo} بنجاح. الحجز محفوظ ولا يتأثر بنتيجة رفع المرفقات.</Alert>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-ink-900">
          رفع مرفقات الحجز ({done} من {items.length})
        </h3>
        <ul className="space-y-2">
          {items.map(f => (
            <li key={f.key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-2">
              <span className="min-w-0 flex-1 truncate text-sm" title={f.file.name}>{f.file.name}</span>
              <span className="text-xs tabular-nums text-ink-500">{humanSize(f.file.size)}</span>
              <span className={`text-xs ${TONE[f.state]}`}>{LABEL[f.state]}</span>
              {f.error && <span role="alert" className="w-full text-xs text-rose-600">{f.error}</span>}
            </li>
          ))}
        </ul>
      </div>

      {failed.length > 0 && !busy && (
        <Alert>
          تعذّر رفع {failed.length} ملف. يمكنك إعادة المحاولة الآن، أو لاحقًا من «مرفقات الحجز» داخل تفاصيل الحجز.
        </Alert>
      )}

      <div className="flex flex-wrap gap-2">
        {failed.length > 0 && (
          <button type="button" className="btn-ghost" disabled={busy} onClick={() => void run(failed)}>
            إعادة محاولة الملفات الفاشلة
          </button>
        )}
        <button type="button" className="btn-primary" disabled={busy} onClick={onFinish}>
          {busy ? 'جارٍ الرفع…' : 'إنهاء'}
        </button>
      </div>
    </div>
  );
}
